import React, { useState, useEffect } from "react";
import { Grid, Input, Icon, Button, Modal, Label } from "semantic-ui-react";
import "semantic-ui-css/semantic.min.css";
import "../../styles/scss/lateFeeIssuance.scss";
import { useLoginCheck } from "../../hooks/useLoginCheck";

function LateFeeIssuancePage() {
  useLoginCheck();
  const [activeTab, setActiveTab] = useState("pending"); // 'pending' | 'history'

  // 発行待ち
  const [pendingGroups, setPendingGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState({}); // { group_key: bool }
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pendingLoading, setPendingLoading] = useState(false);

  // 発行済み履歴
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 発行確認モーダル
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueTargetGroup, setIssueTargetGroup] = useState(null); // 単発の場合
  const [issueBulk, setIssueBulk] = useState(false); // 一括の場合
  const [issuing, setIssuing] = useState(false);

  // 発行結果モーダル
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [issueResults, setIssueResults] = useState(null);

  // 免除モーダル
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [skipTargetGroup, setSkipTargetGroup] = useState(null);
  const [skipReason, setSkipReason] = useState("");
  const [skipping, setSkipping] = useState(false);

  const fetchPendings = async () => {
    try {
      setPendingLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_late_fee_pendings/`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("読み込み失敗");
      const data = await response.json();
      setPendingGroups(data);
      setFilteredGroups(data);
      setSelectedKeys({});
    } catch (error) {
      console.error(error);
      alert("発行待ちリストの取得に失敗しました。");
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_late_fee_history/`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("読み込み失敗");
      const data = await response.json();
      setHistoryList(data);
    } catch (error) {
      console.error(error);
      alert("発行履歴の取得に失敗しました。");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchPendings();
    fetchHistory();
  }, []);

  const handleSearch = (event) => {
    const keyword = searchKeyword;
    if (!keyword) {
      setFilteredGroups(pendingGroups);
      return;
    }
    const filtered = pendingGroups.filter((g) => {
      return (
        (g.customer_name && g.customer_name.includes(keyword)) ||
        (g.customer_name_yomi && g.customer_name_yomi.includes(keyword)) ||
        (g.customer_id && g.customer_id.toString().includes(keyword)) ||
        (g.mail && g.mail.includes(keyword)) ||
        (g.phone && g.phone.includes(keyword))
      );
    });
    setFilteredGroups(filtered);
  };

  const toggleSelect = (groupKey) => {
    setSelectedKeys((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const toggleAll = () => {
    const allSelected = filteredGroups.every((g) => selectedKeys[g.group_key]);
    if (allSelected) {
      setSelectedKeys({});
    } else {
      const next = {};
      filteredGroups.forEach((g) => { next[g.group_key] = true; });
      setSelectedKeys(next);
    }
  };

  const selectedGroups = filteredGroups.filter((g) => selectedKeys[g.group_key]);

  const handleOpenIssueSingle = (group) => {
    setIssueTargetGroup(group);
    setIssueBulk(false);
    setIssueModalOpen(true);
  };

  const handleOpenIssueBulk = () => {
    if (selectedGroups.length === 0) {
      alert("発行対象を選択してください。");
      return;
    }
    setIssueTargetGroup(null);
    setIssueBulk(true);
    setIssueModalOpen(true);
  };

  const handleConfirmIssue = async () => {
    setIssuing(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (issueBulk) {
        // 一括
        const groups = selectedGroups.map((g) => ({ pending_ids: g.pending_ids }));
        const response = await fetch(`${apiUrl}/api/bulk_issue_late_fees/`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groups, issued_by: "admin" }),
        });
        if (!response.ok) throw new Error("発行に失敗しました");
        const data = await response.json();
        setIssueResults(data);
      } else if (issueTargetGroup) {
        // 単発
        const response = await fetch(`${apiUrl}/api/issue_late_fee_invoice/`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pending_ids: issueTargetGroup.pending_ids,
            issued_by: "admin",
          }),
        });
        const data = await response.json();
        setIssueResults({
          summary: { success_count: data.success ? 1 : 0, failed_count: data.success ? 0 : 1 },
          results: [{ ...data, pending_ids: issueTargetGroup.pending_ids }],
        });
      }
      setIssueModalOpen(false);
      setResultModalOpen(true);
    } catch (error) {
      console.error(error);
      alert("発行に失敗しました。");
    } finally {
      setIssuing(false);
    }
  };

  const handleCloseResult = () => {
    setResultModalOpen(false);
    setIssueResults(null);
    fetchPendings();
    fetchHistory();
  };

  const handleOpenSkip = (group) => {
    setSkipTargetGroup(group);
    setSkipReason("");
    setSkipModalOpen(true);
  };

  const handleConfirmSkip = async () => {
    if (!skipTargetGroup) return;
    setSkipping(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/skip_late_fee_pending/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pending_ids: skipTargetGroup.pending_ids,
          reason: skipReason,
        }),
      });
      if (!response.ok) throw new Error("免除に失敗");
      setSkipModalOpen(false);
      fetchPendings();
    } catch (error) {
      console.error(error);
      alert("免除処理に失敗しました。");
    } finally {
      setSkipping(false);
    }
  };

  // 支払い方法は 2 択: card_auto / invoice。旧 card_invoice / bank_transfer も
  // 「請求書払い」へマップ（履歴互換）。
  const paymentMethodLabel = (method) => {
    if (method === "card_auto") return "自動引落";
    return "請求書払い";
  };

  const allSelected =
    filteredGroups.length > 0 &&
    filteredGroups.every((g) => selectedKeys[g.group_key]);

  // 集計
  const selectedTotal = selectedGroups.reduce(
    (acc, g) => acc + g.total_amount,
    0
  );

  return (
    <div className="management-late-fee-issuance">
      <Grid style={{ margin: 0 }}>
        <Grid.Column className="inner">
          <h2>
            <Icon name="exclamation triangle" />
            延滞金発行管理
          </h2>

          {/* タブ */}
          <div className="tab-bar">
            <button
              className={`tab-btn ${activeTab === "pending" ? "active" : ""}`}
              onClick={() => setActiveTab("pending")}
            >
              発行待ち ({pendingGroups.length})
            </button>
            <button
              className={`tab-btn ${activeTab === "history" ? "active" : ""}`}
              onClick={() => setActiveTab("history")}
            >
              発行済み履歴 ({historyList.length})
            </button>
          </div>

          {activeTab === "pending" && (
            <>
              {/* 検索 + 一括発行 */}
              <div className="top-bar">
                <Input
                  type="text"
                  icon="search"
                  placeholder="顧客名・ID・メール・電話番号で検索 (Enter)"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch(e)}
                />
                <div className="right-btns">
                  <Button
                    size="mini"
                    onClick={() => {
                      setSearchKeyword("");
                      setFilteredGroups(pendingGroups);
                    }}
                  >
                    <Icon name="refresh" /> クリア
                  </Button>
                  <Button
                    color="orange"
                    onClick={handleOpenIssueBulk}
                    disabled={selectedGroups.length === 0}
                  >
                    <Icon name="send" />
                    選択した{selectedGroups.length}件を一括発行
                    {selectedGroups.length > 0 && (
                      <span style={{ marginLeft: "0.5em" }}>
                        (¥{selectedTotal.toLocaleString()})
                      </span>
                    )}
                  </Button>
                </div>
              </div>

              {pendingLoading ? (
                <div className="page-loading">
                  <div className="page-loading-spinner"></div>
                </div>
              ) : filteredGroups.length === 0 ? (
                <div className="empty-state">
                  <Icon name="check circle" color="green" size="big" />
                  <p>現在、発行待ちの延滞金はありません。</p>
                </div>
              ) : (
                <>
                  <ul className="pending-grid pending-header">
                    <li className="cell">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                    </li>
                    <li className="cell">顧客</li>
                    <li className="cell">支払方法</li>
                    <li className="cell">支払日</li>
                    <li className="cell">対象請求書</li>
                    <li className="cell">延滞金</li>
                    <li className="cell">事務手数料</li>
                    <li className="cell">合計</li>
                    <li className="cell">操作</li>
                  </ul>

                  {filteredGroups.map((g) => (
                    <ul key={g.group_key} className="pending-grid">
                      <li className="cell">
                        <input
                          type="checkbox"
                          checked={!!selectedKeys[g.group_key]}
                          onChange={() => toggleSelect(g.group_key)}
                        />
                      </li>
                      <li className="cell">
                        <div className="customer-info">
                          <div className="name">{g.customer_name}</div>
                          <div className="yomi">{g.customer_name_yomi}</div>
                          <div className="contact">
                            {g.mail} / {g.phone}
                          </div>
                        </div>
                      </li>
                      <li className="cell">
                        {paymentMethodLabel(g.payment_method)}
                      </li>
                      <li className="cell">{g.payment_date}</li>
                      <li className="cell">
                        <div className="invoice-items">
                          {g.items.map((item) => (
                            <div key={item.pending_id} className="invoice-item">
                              <span className="product">
                                {item.product_name || "請求書"}
                              </span>
                              <span className="amount">
                                ¥{item.original_amount.toLocaleString()}
                              </span>
                              <span className="late">
                                (延滞 {item.late_days}日 / +¥
                                {item.late_fee_amount.toLocaleString()})
                              </span>
                            </div>
                          ))}
                        </div>
                      </li>
                      <li className="cell">
                        ¥{g.total_late_fee.toLocaleString()}
                      </li>
                      <li className="cell">
                        ¥{g.total_admin_fee.toLocaleString()}
                      </li>
                      <li className="cell total">
                        ¥{g.total_amount.toLocaleString()}
                      </li>
                      <li className="cell action-cell">
                        <Button
                          size="mini"
                          color="orange"
                          onClick={() => handleOpenIssueSingle(g)}
                        >
                          発行
                        </Button>
                        <Button
                          size="mini"
                          basic
                          onClick={() => handleOpenSkip(g)}
                        >
                          免除
                        </Button>
                      </li>
                    </ul>
                  ))}
                </>
              )}
            </>
          )}

          {activeTab === "history" && (
            <>
              {historyLoading ? (
                <div className="page-loading">
                  <div className="page-loading-spinner"></div>
                </div>
              ) : historyList.length === 0 ? (
                <div className="empty-state">
                  <p>まだ延滞金請求書は発行されていません。</p>
                </div>
              ) : (
                <>
                  <ul className="history-grid history-header">
                    <li className="cell">発行日</li>
                    <li className="cell">顧客</li>
                    <li className="cell">対象支払日</li>
                    <li className="cell">延滞金</li>
                    <li className="cell">事務手数料</li>
                    <li className="cell">合計</li>
                    <li className="cell">ステータス</li>
                    <li className="cell">発行者</li>
                    <li className="cell">Stripe</li>
                  </ul>
                  {historyList.map((h) => (
                    <ul key={h.id} className="history-grid">
                      <li className="cell">{h.issued_at}</li>
                      <li className="cell">{h.customer_name}</li>
                      <li className="cell">{h.target_payment_date}</li>
                      <li className="cell">¥{h.total_late_fee.toLocaleString()}</li>
                      <li className="cell">¥{h.admin_fee.toLocaleString()}</li>
                      <li className="cell total">
                        ¥{h.total_amount.toLocaleString()}
                      </li>
                      <li className="cell">
                        {h.status === "paid" ? (
                          <Label color="green" size="tiny">支払済み</Label>
                        ) : h.status === "void" ? (
                          <Label color="grey" size="tiny">無効</Label>
                        ) : (
                          <Label color="orange" size="tiny">未払い</Label>
                        )}
                      </li>
                      <li className="cell">{h.issued_by}</li>
                      <li className="cell">
                        {h.invoice_url ? (
                          <a
                            href={h.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Icon name="external alternate" /> 開く
                          </a>
                        ) : (
                          <span className="muted">-</span>
                        )}
                      </li>
                    </ul>
                  ))}
                </>
              )}
            </>
          )}
        </Grid.Column>
      </Grid>

      {/* 発行確認モーダル */}
      <Modal
        open={issueModalOpen}
        onClose={() => setIssueModalOpen(false)}
        size="small"
        className="issue-modal"
      >
        <Modal.Header>
          {issueBulk ? "延滞金請求書 一括発行確認" : "延滞金請求書 発行確認"}
        </Modal.Header>
        <Modal.Content>
          {issueBulk ? (
            <>
              <p className="modal-hint">
                {selectedGroups.length}件の延滞金請求書をStripeで発行します。
                支払い方法が card_auto の顧客は即時課金、それ以外は送信されます。
              </p>
              <ul className="confirm-list">
                {selectedGroups.map((g) => (
                  <li key={g.group_key}>
                    <span className="name">{g.customer_name}</span>
                    <span className="amount">
                      ¥{g.total_amount.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="total-line">
                合計: ¥{selectedTotal.toLocaleString()}
              </div>
            </>
          ) : (
            issueTargetGroup && (
              <>
                <p className="modal-hint">
                  以下の内容で延滞金請求書を発行します。
                </p>
                <div className="detail-box">
                  <div>
                    <b>顧客:</b> {issueTargetGroup.customer_name}
                  </div>
                  <div>
                    <b>支払日:</b> {issueTargetGroup.payment_date}
                  </div>
                  <div>
                    <b>延滞金:</b> ¥
                    {issueTargetGroup.total_late_fee.toLocaleString()}
                  </div>
                  <div>
                    <b>事務手数料:</b> ¥
                    {issueTargetGroup.total_admin_fee.toLocaleString()}
                  </div>
                  <div className="total-line">
                    <b>合計:</b> ¥
                    {issueTargetGroup.total_amount.toLocaleString()}
                  </div>
                </div>
              </>
            )
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setIssueModalOpen(false)}>
            キャンセル
          </Button>
          <Button
            color="orange"
            loading={issuing}
            disabled={issuing}
            onClick={handleConfirmIssue}
          >
            発行する
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 発行結果モーダル */}
      <Modal
        open={resultModalOpen}
        onClose={handleCloseResult}
        size="small"
        className="result-modal"
      >
        <Modal.Header>発行結果</Modal.Header>
        <Modal.Content>
          {issueResults && (
            <>
              <div className="result-summary">
                <Label color="green" size="large">
                  成功 {issueResults.summary.success_count}
                </Label>
                <Label color="red" size="large">
                  失敗 {issueResults.summary.failed_count}
                </Label>
              </div>
              <ul className="result-list">
                {issueResults.results.map((r, idx) => (
                  <li
                    key={idx}
                    className={r.success ? "success" : "failed"}
                  >
                    {r.success ? (
                      <>
                        <Icon name="check circle" color="green" />
                        発行成功 (¥{r.total_amount?.toLocaleString() || "?"})
                        {r.stripe_invoice_id && (
                          <span className="stripe-id">
                            {r.stripe_invoice_id}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <Icon name="warning sign" color="red" />
                        {r.error}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseResult}>閉じる</Button>
        </Modal.Actions>
      </Modal>

      {/* 免除モーダル */}
      <Modal
        open={skipModalOpen}
        onClose={() => setSkipModalOpen(false)}
        size="small"
        className="skip-modal"
      >
        <Modal.Header>延滞金を免除</Modal.Header>
        <Modal.Content>
          {skipTargetGroup && (
            <>
              <p className="modal-hint">
                {skipTargetGroup.customer_name} さんの延滞金
                (¥{skipTargetGroup.total_amount.toLocaleString()}) を免除（skipped）します。
                この操作は取り消せません。
              </p>
              <label>免除理由 (任意)</label>
              <textarea
                value={skipReason}
                onChange={(e) => setSkipReason(e.target.value)}
                rows={2}
                placeholder="例: システム障害でdue_date記録ミス"
              />
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setSkipModalOpen(false)}>
            キャンセル
          </Button>
          <Button
            color="grey"
            loading={skipping}
            disabled={skipping}
            onClick={handleConfirmSkip}
          >
            免除する
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}

export default LateFeeIssuancePage;
